/**
 * Form helpers for create/edit field event views.
 */
import { EVENT_FORMAT_LOCKED, EVENT_ORGANIZER_ROLES } from './event-configs';
import type {
  CreateEventDto,
  EventAttendanceValue,
  EventParticipant,
  EventUnregisteredParticipant,
  EventWithParticipants,
  UnregisteredStudentData,
  UpdateEventDto,
} from '@/types/event.types';
import type { UserWithProfile } from '@/types/user.types';
import { getErrorMessage } from '@/utils/errors-utils';
import { getPrimaryRole } from '@/utils/primary-role-utils';
import { hydratePhoneFromApi } from '@/utils/user-contact-utils';

/** Local form for an unregistered (walk-in) student. */
export type UnregisteredStudentFormValues = {
  firstName: string;
  lastName: string;
  primaryContact: string;
  primaryContactType: 'email' | 'phone' | 'unknown';
  primaryContactCountryCode: string;
  secondaryContact: string;
  secondaryContactCountryCode: string;
};

/** Create-form defaults: format locked to in-person, single occurrence. */
export const DEFAULT_CREATE_EVENT_DATA: Partial<CreateEventDto> = {
  eventFormat: EVENT_FORMAT_LOCKED,
  mode: 'single',
  recurrenceType: 'none',
  planDuringHolidays: true,
};

/** Split selected organizers into the three API id lists. */
export function splitOrganizersByRole(organizers: UserWithProfile[]): {
  tutors: UserWithProfile[];
  volunteers: UserWithProfile[];
  coordinators: UserWithProfile[];
} {
  return {
    tutors: organizers.filter((o) => o.currentRole === 'tutor'),
    volunteers: organizers.filter((o) => o.currentRole === 'volunteer'),
    coordinators: organizers.filter((o) => o.currentRole === 'coordinator'),
  };
}

/** Map local organizer / student / unregistered picks to the create/update DTO. */
export function buildEventParticipantPayload(
  organizers: UserWithProfile[],
  students: UserWithProfile[],
  unregistered: UnregisteredStudentData[],
): Pick<
  CreateEventDto,
  'tutorIds' | 'volunteerIds' | 'coordinatorIds' | 'studentIds' | 'unregisteredStudents'
> {
  const { tutors, volunteers, coordinators } = splitOrganizersByRole(organizers);
  return {
    tutorIds: tutors.map((t) => t.id),
    volunteerIds: volunteers.map((v) => v.id),
    coordinatorIds: coordinators.map((c) => c.id),
    studentIds: students.map((s) => s.id),
    unregisteredStudents: unregistered,
  };
}

/** Date-picker value: keep the YYYY-MM-DD prefix of an ISO string. */
function toDateInputValue(value: Date | string | null | undefined): string | undefined {
  if (!value) return undefined;
  return value.toString().split('T')[0];
}

/** Hydrate the edit form from GET event (format stays locked). */
export function eventToFormData(event: EventWithParticipants): Partial<UpdateEventDto> {
  return {
    establishmentId: event.establishmentId,
    eventDate: toDateInputValue(event.eventDate),
    eventEndDate: toDateInputValue(event.eventEndDate) || null,
    startTime: event.startTime,
    endTime: event.endTime,
    eventFormat: EVENT_FORMAT_LOCKED,
    activityId: event.activityId,
    activityIds: event.activities?.map((a) => a.id) || (event.activityId ? [event.activityId] : []),
    mode: event.mode,
    recurrenceType: event.recurrenceType,
    internalNotes: event.internalNotes,
    roomId: event.roomId,
    levelIds: event.levelIds,
  };
}

/** Split API participants into organizers vs registered students. */
export function extractEventParticipants(participants: EventParticipant[]): {
  organizers: UserWithProfile[];
  students: UserWithProfile[];
} {
  const organizers = participants
    .filter((p) => p.role !== 'student' && p.user)
    .map((p) => ({ ...p.user!, currentRole: p.role as UserWithProfile['currentRole'] }));
  const students = participants
    .filter((p) => p.role === 'student' && p.user)
    .map((p) => ({ ...p.user!, currentRole: 'student' as const }));
  return { organizers, students };
}

/** Map stored unregistered rows back to the local form shape. */
export function mapUnregisteredFromEvent(
  items: EventUnregisteredParticipant[],
): UnregisteredStudentData[] {
  return items.map((p) => ({
    firstName: p.firstName,
    lastName: p.lastName,
    primaryContactType: p.primaryContactType,
    primaryContact: p.primaryContact,
    secondaryContactType: p.secondaryContactType ?? undefined,
    secondaryContact: p.secondaryContact ?? undefined,
  }));
}

/** Default end time = start + 1h, capped at 23:mm. */
export function addOneHour(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const endH = String(Math.min(h + 1, 23)).padStart(2, '0');
  return `${endH}:${String(m).padStart(2, '0')}`;
}

/** Changing start time fills end; moving start past end clears `eventEndDate`. */
export function applyEventFieldSideEffects<T extends Partial<CreateEventDto | UpdateEventDto>>(
  data: T,
  field: string,
  value: unknown,
): T {
  const updated = { ...data, [field]: value } as T;
  if (field === 'startTime' && typeof value === 'string' && value) {
    (updated as Partial<CreateEventDto>).endTime = addOneHour(value);
  }
  if (field === 'eventDate' && data.eventEndDate) {
    const newStartDate = new Date(value as string);
    const currentEndDate = new Date(data.eventEndDate);
    if (currentEndDate < newStartDate) {
      (updated as Partial<CreateEventDto>).eventEndDate = null;
    }
  }
  return updated;
}

/** Generic `{ id }` list → {@link Select} options. */
export function toIdLabelOptions<T extends { id: string }>(
  items: T[],
  getLabel: (item: T) => string,
): Array<{ value: string; label: string }> {
  return items.map((item) => ({ value: item.id, label: getLabel(item) }));
}

/** Picker groups that count as organizers (`tutors` / `volunteers` / `admins`). */
export function isOrganizerRoleGroup(roleGroup: string | undefined): boolean {
  return roleGroup === 'tutors' || roleGroup === 'volunteers' || roleGroup === 'admins';
}

/** Map a picker group id to `currentRole` (`admins` → `coordinator`). */
export function roleGroupToOrganizerRole(
  roleGroup: string | undefined,
): UserWithProfile['currentRole'] {
  if (roleGroup === 'admins') return 'coordinator';
  if (roleGroup === 'volunteers') return 'volunteer';
  return 'tutor';
}

/** Seed the attendance control from stored statuses (`pending` when missing). */
export function initialAttendanceMap(
  participants: Array<{ id: string; attendanceStatus?: string | null }>,
  unregistered: Array<{ id: string; attendanceStatus?: string | null }>,
): Record<string, EventAttendanceValue> {
  const initial: Record<string, EventAttendanceValue> = {};
  for (const p of participants) {
    initial[p.id] = (p.attendanceStatus as EventAttendanceValue) || 'pending';
  }
  for (const p of unregistered) {
    initial[p.id] = (p.attendanceStatus as EventAttendanceValue) || 'pending';
  }
  return initial;
}

/** Split attendance rows into organizers vs students. */
export function groupEventAttendanceParticipants<T extends { role: string }>(
  participants: T[],
): { organizers: T[]; students: T[] } {
  const organizerRoles: readonly string[] = EVENT_ORGANIZER_ROLES;
  return {
    organizers: participants.filter((p) => organizerRoles.includes(p.role)),
    students: participants.filter((p) => p.role === 'student'),
  };
}

/** PATCH body for every participant id (default `pending`). */
export function buildAttendanceUpdates(
  items: Array<{ id: string }>,
  localStatus: Record<string, EventAttendanceValue>,
): Array<{ id: string; attendanceStatus: EventAttendanceValue }> {
  return items.map((p) => ({
    id: p.id,
    attendanceStatus: localStatus[p.id] ?? 'pending',
  }));
}

/** First + last name, trimmed (empty parts dropped). */
export function formatEventParticipantName(
  firstName?: string | null,
  lastName?: string | null,
): string {
  return `${firstName ?? ''} ${lastName ?? ''}`.trim();
}

/** Organizer subtitle: display role label, else the participant `role`. */
export function resolveEventOrganizerSubtitle(participant: EventParticipant): string {
  return getPrimaryRole(participant.user?.userRoles)?.label ?? participant.role;
}

/** Drop contact fields when type is `unknown`; secondary is the opposite of primary. */
export function buildUnregisteredStudentPayload(formData: {
  firstName: string;
  lastName: string;
  primaryContact: string;
  primaryContactType: 'email' | 'phone' | 'unknown';
  secondaryContact: string;
}): UnregisteredStudentData {
  const hasContact = Boolean(formData.primaryContact) && formData.primaryContactType !== 'unknown';
  const oppositeType = formData.primaryContactType === 'email' ? 'phone' : 'email';
  const secondaryType = hasContact && formData.secondaryContact ? oppositeType : undefined;
  return {
    firstName: formData.firstName,
    lastName: formData.lastName,
    primaryContactType: hasContact ? (formData.primaryContactType as 'email' | 'phone') : undefined,
    primaryContact: hasContact ? formData.primaryContact : undefined,
    secondaryContactType: secondaryType,
    secondaryContact: hasContact ? formData.secondaryContact || undefined : undefined,
  };
}

/** Central error copy from {@link getErrorMessage}. */
export function getEventApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Next contact fields; changing type clears the secondary contact. */
export function applyUnregisteredStudentContactChange(
  prev: UnregisteredStudentFormValues,
  value: string,
  type: UnregisteredStudentFormValues['primaryContactType'],
  defaultCountry: string,
  countryCode?: string,
): UnregisteredStudentFormValues {
  const typeChanged =
    type !== 'unknown' && prev.primaryContactType !== 'unknown' && type !== prev.primaryContactType;
  return {
    ...prev,
    primaryContact: value,
    primaryContactType: type,
    primaryContactCountryCode: countryCode || prev.primaryContactCountryCode,
    secondaryContact: typeChanged ? '' : prev.secondaryContact,
    secondaryContactCountryCode: typeChanged ? defaultCountry : prev.secondaryContactCountryCode,
  };
}

/** Keep the currently selected activity in the options if filters hide it. */
export function mergeEventActivitiesWithInitial<T extends { id: string }>(
  base: T[],
  initialActivity: T | null | undefined,
  selectedActivityId: string | undefined,
): T[] {
  if (!initialActivity || !selectedActivityId) return base;
  if (base.some((activity) => activity.id === selectedActivityId)) return base;
  return [initialActivity, ...base];
}

/** Prefer `activityIds`; else wrap a single `activityId`. */
export function resolveSelectedActivityIds(form: {
  activityIds?: string[];
  activityId?: string;
}): string[] {
  if (form.activityIds) return form.activityIds;
  return form.activityId ? [form.activityId] : [];
}

/** Hydrate the unregistered-student form from stored event data. */
export function hydrateUnregisteredStudentForm(
  initialData: UnregisteredStudentData | undefined,
  defaultCountry: string,
): UnregisteredStudentFormValues {
  const primaryType = (initialData?.primaryContactType ?? 'unknown') as
    | 'email'
    | 'phone'
    | 'unknown';
  const primary =
    primaryType === 'phone'
      ? hydratePhoneFromApi(initialData?.primaryContact, defaultCountry)
      : { value: initialData?.primaryContact ?? '', countryCode: defaultCountry };
  const secondaryIsPhone = primaryType === 'email' || initialData?.secondaryContactType === 'phone';
  const secondary = secondaryIsPhone
    ? hydratePhoneFromApi(initialData?.secondaryContact, defaultCountry)
    : { value: initialData?.secondaryContact ?? '', countryCode: defaultCountry };

  return {
    firstName: initialData?.firstName ?? '',
    lastName: initialData?.lastName ?? '',
    primaryContact: primary.value,
    primaryContactType: primaryType,
    primaryContactCountryCode: primary.countryCode,
    secondaryContact: secondary.value,
    secondaryContactCountryCode: secondary.countryCode,
  };
}

/** Create DTO: locked format plus organizer / student / unregistered ids. */
export function buildCreateEventDto(
  eventData: Partial<CreateEventDto>,
  organizers: UserWithProfile[],
  students: UserWithProfile[],
  unregistered: UnregisteredStudentData[],
): CreateEventDto {
  return {
    ...(eventData as CreateEventDto),
    eventFormat: EVENT_FORMAT_LOCKED,
    ...buildEventParticipantPayload(organizers, students, unregistered),
  };
}

/** Update DTO: locked format plus organizer / student / unregistered ids. */
export function buildUpdateEventDto(
  eventData: Partial<UpdateEventDto>,
  organizers: UserWithProfile[],
  students: UserWithProfile[],
  unregistered: UnregisteredStudentData[],
): UpdateEventDto {
  return {
    ...(eventData as UpdateEventDto),
    eventFormat: EVENT_FORMAT_LOCKED,
    ...buildEventParticipantPayload(organizers, students, unregistered),
  };
}
